import type {
  AssertionResult,
  CaseExpectations,
  JsonAssertion,
  PlaygroundResponse
} from "./types"

/** Resolves `items[0].name` style paths; returns undefined when any hop misses. */
export function resolvePath(source: unknown, path: string): unknown {
  const segments = path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean)

  let current: unknown = source
  for (const segment of segments) {
    if (current === null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return current
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (a === null || b === null) return false
  if (typeof a !== "object") return false
  return JSON.stringify(a) === JSON.stringify(b)
}

function preview(value: unknown, max = 120): string {
  const text = typeof value === "string" ? value : JSON.stringify(value)
  if (text === undefined) return "undefined"
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function structuredOutput(response: PlaygroundResponse): unknown {
  if (response.parsed !== undefined) return response.parsed
  try {
    return JSON.parse(response.output)
  } catch {
    return undefined
  }
}

function evaluateJsonAssertion(
  assertion: JsonAssertion,
  root: unknown
): AssertionResult {
  const actual = resolvePath(root, assertion.path)

  if (assertion.exists !== undefined) {
    const exists = actual !== undefined && actual !== null
    return {
      label: `json ${assertion.path} ${assertion.exists ? "exists" : "absent"}`,
      passed: exists === assertion.exists,
      detail: `actual: ${preview(actual)}`
    }
  }

  if (assertion.equals !== undefined) {
    return {
      label: `json ${assertion.path} == ${preview(assertion.equals, 40)}`,
      passed: deepEqual(actual, assertion.equals),
      detail: `actual: ${preview(actual)}`
    }
  }

  if (assertion.notEquals !== undefined) {
    return {
      label: `json ${assertion.path} != ${preview(assertion.notEquals, 40)}`,
      passed: !deepEqual(actual, assertion.notEquals),
      detail: `actual: ${preview(actual)}`
    }
  }

  if (assertion.oneOf !== undefined) {
    return {
      label: `json ${assertion.path} in ${preview(assertion.oneOf, 60)}`,
      passed: assertion.oneOf.some((option) => deepEqual(actual, option)),
      detail: `actual: ${preview(actual)}`
    }
  }

  if (assertion.contains !== undefined) {
    const text = typeof actual === "string" ? actual : JSON.stringify(actual)
    return {
      label: `json ${assertion.path} contains "${assertion.contains}"`,
      passed: Boolean(text?.includes(assertion.contains)),
      detail: `actual: ${preview(actual)}`
    }
  }

  return {
    label: `json ${assertion.path}`,
    passed: false,
    detail: "assertion has no operator (equals/notEquals/exists/oneOf/contains)"
  }
}

export function evaluateExpectations(
  response: PlaygroundResponse,
  expect?: CaseExpectations
): AssertionResult[] {
  const results: AssertionResult[] = []

  if (!response.ok) {
    return [
      { label: "request", passed: false, detail: response.error || "failed" }
    ]
  }

  if (!expect) return results

  const normalize = (text: string) =>
    expect.caseSensitive ? text : text.toLocaleLowerCase("tr")
  const haystack = normalize(response.output)

  for (const needle of expect.contains ?? []) {
    results.push({
      label: `contains "${needle}"`,
      passed: haystack.includes(normalize(needle))
    })
  }

  for (const needle of expect.notContains ?? []) {
    results.push({
      label: `not contains "${needle}"`,
      passed: !haystack.includes(normalize(needle))
    })
  }

  if (expect.regex) {
    try {
      const pattern = new RegExp(
        expect.regex,
        expect.caseSensitive ? "s" : "is"
      )
      results.push({
        label: `regex /${expect.regex}/`,
        passed: pattern.test(response.output)
      })
    } catch (error) {
      results.push({
        label: `regex /${expect.regex}/`,
        passed: false,
        detail: `invalid regex: ${String(error)}`
      })
    }
  }

  if (expect.json?.length) {
    const root = structuredOutput(response)
    if (root === undefined) {
      results.push({
        label: "json output",
        passed: false,
        detail: "output is not valid JSON"
      })
    } else {
      for (const assertion of expect.json) {
        results.push(evaluateJsonAssertion(assertion, root))
      }
    }
  }

  if (expect.maxLatencyMs) {
    results.push({
      label: `latency <= ${expect.maxLatencyMs}ms`,
      passed: response.latencyMs <= expect.maxLatencyMs,
      detail: `actual: ${response.latencyMs}ms`
    })
  }

  return results
}
